<template>
  <div class="database-dashboard">
    <div v-if="loading" class="loading-state">
      <p>Loading database information...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadData">Retry</button>
    </div>

    <div v-else class="database-content">
      <!-- Database Usage Chart -->
      <div v-if="databaseData" class="chart-section">
        <div class="chart-container">
          <h3>Database Usage</h3>
          <RingChart
            :percentage="databaseData.usagePercentage"
            :used="formatBytes(databaseData.usedBytes)"
            :total="formatBytes(databaseData.totalAllocatedBytes)"
            label="Used"
            :size="250"
          />
          <div class="usage-details">
            <div class="usage-detail">
              <span class="usage-label">Used:</span>
              <span class="usage-value"
                >{{ formatBytes(databaseData.usedBytes) }} ({{
                  databaseData.usagePercentage
                }}%)</span
              >
            </div>
            <div class="usage-detail">
              <span class="usage-label">Available:</span>
              <span class="usage-value">{{ formatBytes(databaseData.availableBytes) }}</span>
            </div>
            <div class="usage-detail">
              <span class="usage-label">Total Allocated:</span>
              <span class="usage-value">{{ formatBytes(databaseData.totalAllocatedBytes) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Table Breakdown -->
      <div v-if="databaseData && databaseData.tableBreakdown.length > 0" class="table-breakdown">
        <div class="section-header">
          <h3>Table Size Breakdown</h3>
        </div>
        <div class="table-container">
          <table class="breakdown-table">
            <thead>
              <tr>
                <th>Table Name</th>
                <th>Size</th>
                <th>% of Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="table in databaseData.tableBreakdown" :key="table.tableName">
                <td class="table-name">{{ table.tableName }}</td>
                <td class="table-size">{{ formatBytes(table.sizeBytes) }}</td>
                <td class="table-percent">{{ getTablePercentage(table.sizeBytes) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { client, type DatabaseInfo, parseErrorMessage } from '../client.js';
import RingChart from './RingChart.vue';

const loading = ref(true);
const error = ref<string | null>(null);
const databaseData = ref<DatabaseInfo | null>(null);

// Helper function to convert bytes to human readable format
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  if (i === 0) return bytes + ' ' + sizes[i];

  const value = bytes / Math.pow(k, i);
  const decimals = i === 1 ? 0 : i === 2 ? 2 : 2; // KB: no decimals, MB/GB: 2 decimals

  return value.toFixed(decimals) + ' ' + sizes[i];
};

// Calculate percentage of total allocated database size for each table
const getTablePercentage = (sizeBytes: number): string => {
  if (!databaseData.value?.totalAllocatedBytes) return '0.0';
  const percentage = (sizeBytes / databaseData.value.totalAllocatedBytes) * 100;
  return percentage.toFixed(3);
};

const loadData = async () => {
  if (!client.value) return;

  loading.value = true;
  error.value = null;

  try {
    databaseData.value = await client.value.getDatabase();
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    console.error('Failed to load database data:', err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadData();
});
</script>

<style scoped>
.loading-state,
.error-state {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.error-state p {
  color: #dc2626;
  margin-bottom: 1rem;
}

.retry-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.chart-section {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  padding: 2rem;
  margin-bottom: 2rem;
}

.chart-container h3 {
  margin: 0 0 2rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
}

.chart-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.usage-details {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 200px;
}

.usage-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.usage-label {
  font-weight: 500;
  color: #6b7280;
}

.usage-value {
  font-weight: 600;
  color: #1f2937;
}

/* Table Breakdown Section */
.table-breakdown {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: 1.5rem;
}

.section-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.section-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.table-container {
  overflow-x: auto;
}

.breakdown-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.breakdown-table th,
.breakdown-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.breakdown-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.breakdown-table tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.breakdown-table tbody tr:hover {
  background-color: #f3f4f6;
}

.breakdown-table .table-name {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  color: #1f2937;
  font-weight: 500;
}

.breakdown-table .table-size,
.breakdown-table .table-percent {
  text-align: right;
  color: #6b7280;
  font-weight: 500;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .database-dashboard {
    padding: 0;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .dashboard-header h2 {
    text-align: center;
  }

  .chart-section {
    padding: 1.5rem;
  }

  .usage-details {
    width: 100%;
    min-width: unset;
  }
}

@media (max-width: 480px) {
  .chart-section {
    padding: 1rem;
  }
}
</style>
